/**
 * JobPostHeader Component
 *
 * Sticky header for job post creation with:
 * - Back navigation with dynamic title
 * - Mini stepper for step navigation (desktop/tablet)
 * - Dropdown for step navigation (mobile)
 * - Conditional rendering based on AI generation status
 * - Smooth fade-in animation
 *
 * Following SOLID principles and mobile-first approach
 */

import React, { useCallback } from "react";
import {
  Button,
  Flex,
  Typography,
  Select,
  Spin,
  Steps,
  Tooltip,
  Modal,
  message,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import { PersistenceStatus } from "../../hooks/useFormPersistence";
import { JobPostFormData } from "../../schemas/jobPostValidation";
import {
  validateStep1Complete,
  validateStep2Complete,
  validateStep3Complete,
  validateStep5Complete,
  calculateCompletionPercentage,
  calculateStep2CompletionPercentage,
  calculateStep3CompletionPercentage,
  calculateStep5CompletionPercentage,
} from "../../hooks/useFormValidation";
import "./index.scss";

const { Text } = Typography;

interface JobPostHeaderProps {
  /** Whether header should be visible (after AI generation) */
  isVisible: boolean;
  /** Current active step (0-6) */
  currentStep: number;
  /** Form mode: new, edit, or clone */
  mode: "new" | "edit" | "clone";
  /** Whether form has unsaved changes */
  isDirty: boolean;
  /** Callback for step navigation */
  onStepChange: (step: number) => void;
  /** Step titles for navigation labels */
  stepTitles: string[];
  /** Total number of steps */
  totalSteps: number;
  /** Array of completed step numbers */
  completedSteps?: number[];
  /** Save status for auto-save indicator */
  saveStatus?: PersistenceStatus;
  /** Form data for validation */
  formData?: Partial<JobPostFormData>;

  handlePrevious: () => void;
}

const JobPostHeader: React.FC<JobPostHeaderProps> = ({
  isVisible,
  currentStep,
  mode,
  isDirty,
  onStepChange,
  stepTitles,
  totalSteps,
  completedSteps = [],
  saveStatus,
  formData: propFormData,
  handlePrevious,
}) => {
  const navigate = useNavigate();

  // Try to get formData from context if not provided as prop
  let formContextData: Partial<JobPostFormData> | undefined;
  try {
    const formContext = useFormContext<JobPostFormData>();
    formContextData = formContext.watch();
  } catch {
    // Form context not available, use prop
  }

  // Use prop formData or context formData
  const formData = propFormData || formContextData;

  // Format last saved time
  const formatLastSaved = (timestamp?: number) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60)
      return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    return date.toLocaleString();
  };

  // Check if step is complete (using validation functions)
  const isStepComplete = useCallback(
    (step: number): boolean => {
      if (!formData) {
        // Fallback to completedSteps if formData not available
        return completedSteps.includes(step);
      }

      switch (step) {
        case 1:
          return validateStep1Complete(formData.step1);
        case 2:
          return validateStep2Complete(formData.step2);
        case 3:
          return validateStep3Complete(formData.step3);
        case 4:
          // HR Screening is optional, always considered complete
          return true;
        case 5:
          return validateStep5Complete(formData.step5);
        default:
          return completedSteps.includes(step);
      }
    },
    [formData, completedSteps]
  );

  // Check if step has been visited (has data but validation failed)
  const isStepVisited = useCallback(
    (step: number): boolean => {
      if (!formData) return false;

      switch (step) {
        case 1:
          return !!(
            formData.step1?.title ||
            formData.step1?.position ||
            formData.step1?.description
          );
        case 2:
          return !!(
            formData.step2?.requirements &&
            formData.step2.requirements.length > 0
          );
        case 3:
          return !!(
            formData.step3?.interviewRounds &&
            formData.step3.interviewRounds.length > 0
          );
        case 4:
          return !!(
            formData.step4?.manualScreeningQuestions &&
            formData.step4.manualScreeningQuestions.length > 0
          );
        case 5:
          return !!(
            formData.step5?.onlineInterview !== undefined ||
            formData.step5?.interviewers
          );
        default:
          return false;
      }
    },
    [formData]
  );

  // Get page title based on mode
  const getPageTitle = () => {
    switch (mode) {
      case "edit":
        return "Edit Job Post";
      case "clone":
        return "Clone Job Post";
      default:
        return "Create Job Post";
    }
  };

  // Handle back navigation
  // Navigate to job posts list instead of browser history to avoid going to sign-in page
  const handleBackNavigation = useCallback(() => {
    if (currentStep === 1 && isDirty && mode === "new") {
      handlePrevious();
      return;
    }

    if (isDirty && currentStep > 0) {
      Modal.confirm({
        title: "Unsaved Changes",
        content: "You have unsaved changes. Are you sure you want to leave?",
        okText:
          mode === "edit" ? "Leave without Saving" : "Save as Draft and Leave",
        okButtonProps: {
          type: "default",
        },
        cancelText: "Stay",
        centered: true,
        onOk: () => {
          const backUrl =
            mode === "edit" || mode === "clone"
              ? formData?.jobPostId
                ? `/job-posts/${formData?.jobPostId}`
                : "/job-posts"
              : "/job-posts";
          // Drafts are automatically saved when leaving the page
          if (mode !== "edit") {
            message.success("Draft saved successfully");
          }
          navigate(backUrl);
        },
      });
      return;
    }

    const backUrl =
      mode === "edit" || mode === "clone"
        ? `/job-posts/${formData?.jobPostId || ""}`
        : "/job-posts";
    // Navigate to job posts list page instead of using browser history
    // This ensures we don't accidentally go to sign-in page if there's no history
    navigate(backUrl);
  }, [isDirty, navigate, mode, formData?.jobPostId, currentStep]);

  // Handle step selection from stepper
  const handleStepClick = useCallback(
    (step: number) => {
      // Steps component onChange passes 0-indexed step, so we add 1 to get actual step number
      onStepChange(step + 1);
    },
    [onStepChange]
  );

  // Handle step selection from dropdown
  const handleDropdownChange = useCallback(
    (value: number) => {
      onStepChange(value);
    },
    [onStepChange]
  );

  // Determine step status for visual indicators - calculate directly without memoization
  // Priority: Current active step > Validation status
  // This function is called directly in render to ensure immediate updates
  const getStepStatus = (
    stepNumber: number
  ): "wait" | "process" | "finish" | "error" => {
    const isComplete = isStepComplete(stepNumber);
    const isVisited = isStepVisited(stepNumber);
    const isActive = stepNumber === currentStep;

    // Current active step takes priority - show primary/blue
    if (isActive) {
      // If active, show process (blue) regardless of completion status
      return "process";
    } else if (isComplete) {
      // If complete and not active, show success (green)
      return "finish";
    } else if (isVisited && !isComplete) {
      // If visited but incomplete and not active, show error (red)
      return "error";
    } else {
      // Not started
      return "wait";
    }
  };

  // Create dropdown options for mobile with validation status colors
  // Calculate directly in render for immediate updates
  // Use totalSteps - 1 because Step 0 (AI Generator) is not shown in the stepper
  const dropdownOptions = Array.from({ length: totalSteps - 1 }, (_, index) => {
    const stepNumber = index + 1;
    const stepTitle = stepTitles[stepNumber] || `Step ${stepNumber}`;
    const status = getStepStatus(stepNumber);

    // Determine color based on status using theme variables
    let labelColor = "var(--ant-color-text-secondary)"; // wait - gray
    if (status === "finish") {
      labelColor = "var(--ant-color-success)"; // green
    } else if (status === "process") {
      labelColor = "var(--ant-color-primary)"; // blue
    } else if (status === "error") {
      labelColor = "var(--ant-color-error)"; // red
    }

    return {
      label: (
        <span style={{ color: labelColor }}>
          Step {stepNumber}: {stepTitle}
        </span>
      ),
      value: stepNumber,
    };
  });

  // Calculate completion percentage for a specific step
  const getStepPercentage = useCallback(
    (stepNumber: number): number => {
      if (!formData || stepNumber === 0) return 0;

      switch (stepNumber) {
        case 1:
          return calculateCompletionPercentage(formData.step1);
        case 2:
          return calculateStep2CompletionPercentage(formData.step2);
        case 3:
          return calculateStep3CompletionPercentage(formData.step3);
        case 4:
          // HR Screening is optional, return 100 if visited, 0 if not
          return formData.step4?.manualScreeningQuestions &&
            formData.step4.manualScreeningQuestions.length > 0
            ? 100
            : 0;
        case 5:
          return calculateStep5CompletionPercentage(formData.step5);
        case 6:
          // Review step - always 100% if reached
          return 100;
        default:
          return 0;
      }
    },
    [formData]
  );

  // Calculate completion percentage for current step
  const getCurrentStepPercentage = useCallback((): number => {
    return getStepPercentage(currentStep);
  }, [getStepPercentage, currentStep]);

  // Always render header when visible, even for Step 0
  // Stepper will be hidden for Step 0 via conditional rendering below

  if (!isVisible) {
    return null;
  }

  // Create Steps items for inline stepper
  // Use totalSteps - 1 because Step 0 (AI Generator) is not shown in the stepper
  const stepsItems = Array.from({ length: totalSteps - 1 }, (_, index) => {
    const stepNumber = index + 1;
    const stepTitle = stepTitles[stepNumber] || `Step ${stepNumber}`;
    const status = getStepStatus(stepNumber);

    // Map status to Steps component status type
    let stepStatus: "error" | "wait" | "process" | "finish" | undefined;
    if (status === "error") {
      stepStatus = "error";
    } else if (status === "finish") {
      stepStatus = "finish";
    } else if (status === "process") {
      stepStatus = "process";
    } else {
      stepStatus = "wait";
    }

    // Use step title directly without percentage
    const displayTitle = stepTitle;
    const fullTitle = stepTitle; // For tooltip

    return {
      title: (
        <Tooltip title={fullTitle} placement="top">
          <span className="step-title-text">{displayTitle}</span>
        </Tooltip>
      ),
      status: stepStatus,
    };
  });

  // Get dynamic percentage for current step
  const currentStepPercent = getCurrentStepPercentage();

  return (
    <header className="job-post-header">
      <div className="header-container">
        {/* Row 1: Back Navigation + Title + Save Status (Desktop) / Back + Title + Dropdown (Mobile) */}
        <div className="header-row-1">
          <Flex align="center" gap="small" className="header-left">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={handleBackNavigation}
              className="back-button"
              aria-label="Go back"
            />
            <div className="title-section">
              <Typography.Title
                level={4}
                className="page-title"
                style={{ marginBottom: 0 }}
              >
                {getPageTitle()}
              </Typography.Title>
            </div>
            {isDirty && currentStep > 0 && (
              <span className="unsaved-indicator" aria-label="Unsaved changes">
                •
              </span>
            )}
          </Flex>

          {/* Right side: Save status + Dropdown (Mobile) */}
          <Flex align="center" gap="small" className="header-right">
            {/* Save status indicator - right end of title row (both desktop and mobile) */}
            {saveStatus && currentStep > 0 && (
              <Text
                type="secondary"
                className="save-status"
                style={{ fontSize: 12 }}
              >
                {saveStatus.status === "saving" && (
                  <span>
                    <Spin size="small" style={{ marginRight: 4 }} /> Saving...
                  </span>
                )}
                {saveStatus.status === "saved" && saveStatus.lastSavedAt && (
                  <span>
                    Last saved {formatLastSaved(saveStatus.lastSavedAt)}
                  </span>
                )}
                {saveStatus.status === "error" && (
                  <span
                    style={{ color: "var(--ant-color-error)" }}
                    title={saveStatus.error}
                  >
                    ⚠️ Save failed
                  </span>
                )}
              </Text>
            )}

            {/* Mobile: Dropdown (< 768px) */}
            {currentStep > 0 && (
              <div className="header-right-mobile">
                <Select
                  value={currentStep}
                  onChange={handleDropdownChange}
                  options={dropdownOptions}
                  size="middle"
                  className="step-select"
                  style={{ minWidth: 150 }}
                />
              </div>
            )}
          </Flex>
        </div>

        {/* Row 2: Stepper - Desktop only */}
        {currentStep > 0 && (
          <div className="header-row-2-desktop">
            <Steps
              current={currentStep - 1}
              items={stepsItems}
              size="small"
              onChange={handleStepClick}
              className="header-steps"
              type="navigation"
              percent={currentStepPercent}
            />
          </div>
        )}
      </div>
    </header>
  );
};

export default JobPostHeader;
